import React, {Component} from 'react';
import ImageUpload from './imageupload/ImageUpload';
import InsuranceOption from './insurance/InsuranceOption.js';
import SkipStep from './components/SkipStep';
import withWorkflow from './components/withWorkflow';
import {scrollToMessage} from './util/scrollToMessage';
import RequiredFieldDisclaimer from './components/RequiredFieldDisclaimer';
import {isGovernmentPayorOrder} from './util/gearOrderUtil';
import MedicareOrderAlert from './components/MedicareOrderAlert';
import AccessionedOrderAlert from './components/AccessionedOrderAlert';
import {isMedicareOrTricareSubscriberId} from './util/MedicareUtil';
import MedicareConfirmation from './insurance/MedicareConfirmation';
import PhoneInput from 'react-phone-number-input/input';
import {formatInitialPhoneNumber, formatPhoneNumberForSave} from './patient/PatientInfoForm';
import DateOfBirthInput from './components/DateOfBirthInput';
import FieldLevelHelp from './components/FieldLevelHelp';

class Insurance extends Component {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.submitInsuranceInfo = this.submitInsuranceInfo.bind(this);
    this.goBack = this.goBack.bind(this);
    this.showUploadError = this.showUploadError.bind(this);
    this.selectOption = this.selectOption.bind(this);
    this.toggleSecondaryInsurance = this.toggleSecondaryInsurance.bind(this);
    this.addUploadedImage = this.addUploadedImage.bind(this);
    this.removeUploadedImage = this.removeUploadedImage.bind(this);
    this.setFormValue = this.setFormValue.bind(this);
    this.setSecondaryFormValue = this.setSecondaryFormValue.bind(this);
    this.setSecondaryProvider = this.setSecondaryProvider.bind(this);
    this.validate = this.validate.bind(this);

    // SCR-6308: Lock the page if any requisition on the order has been accessioned
    const locked = this.props.userInfo.adminUser !== 'true' && this.props.gearOrder && this.props.gearOrder.requisitions
        && this.props.gearOrder.requisitions.some(req => req.accessionNo);

    /**
     * Pre-fill the insurance fields using the order's insurance object if it exists.
     * Otherwise, if a single insurance option is passed in on props, pre-fill with those details.
     */
    let insurance = {};
    let secondaryInsurance = {};
    let claimsAddress = {};
    let secondaryClaimsAddress = {};

    if (this.props.gearOrder && this.props.gearOrder.insurance) {
      insurance = this.props.gearOrder.insurance;
      claimsAddress = insurance.claimsAddress || {};
    } else if (this.props.options && this.props.options.length === 1) {
      insurance = this.props.options[0];
      claimsAddress = insurance.claimsAddress || {};
      insurance.insuranceProvider = insurance.insuranceProvider || 'other';
    }

    if (this.props.gearOrder && this.props.gearOrder.secondaryInsurance) {
      secondaryInsurance = this.props.gearOrder.secondaryInsurance;
      secondaryClaimsAddress = secondaryInsurance.claimsAddress || {};
    }

    // Default to Medicare for Medicare orders when no provider is selected
    let defaultProvider = insurance.insuranceProvider;
    if (isGovernmentPayorOrder(this.props.gearOrder) && !defaultProvider) {
      defaultProvider = 'medicare';
    }

    let form = {};
    form.provider = {value: defaultProvider || ''};
    form.subscriberId = {value: insurance.subscriberId || ''};
    form.subscriberName = {value: insurance.subscriberName || ''};
    form.subscriberDOB = {value: insurance.subscriberDOB || ''};
    form.groupNumber = {value: insurance.groupNumber || ''};
    form.subscriberRelation = {value: insurance.subscriberRelation || ''};
    form.otherSubscriberRelation = {value: insurance.otherSubscriberRelation || ''};
    form.otherProvider = {value: insurance.otherProvider || ''};
    form.phoneNumber = {value: formatInitialPhoneNumber(insurance.phoneNumber)};
    form.address1 = {value: claimsAddress.address1 || ''};
    form.address2 = {value: claimsAddress.address2 || ''};
    form.city = {value: claimsAddress.city || ''};
    form.state = {value: claimsAddress.state || ''};
    form.zipCode = {value: claimsAddress.zipCode || ''};
    form.country = {value: claimsAddress.country || ''};
    form.county = {value: claimsAddress.county || ''};

    let secondaryForm = {};
    secondaryForm.provider = {value: secondaryInsurance.insuranceProvider || ''};
    secondaryForm.subscriberId = {value: secondaryInsurance.subscriberId || ''};
    secondaryForm.subscriberName = {value: secondaryInsurance.subscriberName || ''};
    secondaryForm.subscriberDOB = {value: secondaryInsurance.subscriberDOB || ''};
    secondaryForm.groupNumber = {value: secondaryInsurance.groupNumber || ''};
    secondaryForm.subscriberRelation = {value: secondaryInsurance.subscriberRelation || ''};
    secondaryForm.otherSubscriberRelation = {value: secondaryInsurance.otherSubscriberRelation || ''};
    secondaryForm.otherProvider = {value: secondaryInsurance.otherProvider || ''};
    secondaryForm.phoneNumber = {value: formatInitialPhoneNumber(secondaryInsurance.phoneNumber)};
    secondaryForm.address1 = {value: secondaryClaimsAddress.address1 || ''};
    secondaryForm.address2 = {value: secondaryClaimsAddress.address2 || ''};
    secondaryForm.city = {value: secondaryClaimsAddress.city || ''};
    secondaryForm.state = {value: secondaryClaimsAddress.state || ''};
    secondaryForm.zipCode = {value: secondaryClaimsAddress.zipCode || ''};
    secondaryForm.country = {value: secondaryClaimsAddress.country || ''};
    secondaryForm.county = {value: secondaryClaimsAddress.county || ''};

    this.state = {
      showError: false,
      showUploadError: false,
      errorMessage: '',
      uploadErrorMessage: '',
      secondaryInsurance: !!this.props.gearOrder.secondaryInsurance,
      showOptions: this.props.options.length > 0,
      pageLocked: locked,
      form: form,
      secondaryForm: secondaryForm,
      possibleMBI: (isMedicareOrTricareSubscriberId(insurance.subscriberId) ? insurance.subscriberId : null),
      dirty: false,
    };
  }

  goBack(e) {
    if (this.props.doConfirm(e)) {
      this.props.updateStep(this.props.gearOrder, 'insurance', 'Back');
    } else {
      e.preventDefault();
    }
  }

  showUploadError(msg) {
    this.setState({
      showUploadError: true,
      uploadErrorMessage: msg
    });

  }

  setFormValue(e) {
    this.setDirty(true);
    let form = Object.assign({}, this.state.form);
    form[e.currentTarget.dataset.fieldname].value = e.currentTarget.value;
    this.setState({
      form: form
    });
  }

  setSecondaryFormValue(e) {
    this.setDirty(true);
    let secondaryForm = Object.assign({}, this.state.secondaryForm);
    secondaryForm[e.currentTarget.dataset.fieldname].value = e.currentTarget.value;
    this.setState({
      secondaryForm: secondaryForm
    });
  }

  setSecondaryProvider(e) {
    let provider = e.currentTarget.value;
    if (provider === 'medicare' || provider === 'medicaid' || provider === 'tricare' || provider === 'medicare_railroad') {
      this.setState({
        mbiConfirmed: true,
      });
    }
    this.setSecondaryFormValue(e)
  }

  clearSecondaryInsurance() {
    let secondaryForm = {};
    secondaryForm.provider = {value: ''};
    secondaryForm.subscriberId = {value: ''};
    secondaryForm.subscriberName = {value: ''};
    secondaryForm.subscriberDOB = {value: ''};
    secondaryForm.groupNumber = {value:  ''};
    secondaryForm.subscriberRelation = {value: ''};
    secondaryForm.otherSubscriberRelation = {value: ''};
    secondaryForm.otherProvider = {value:  ''};
    secondaryForm.phoneNumber = {value: ''};
    secondaryForm.address1 = {value: ''};
    secondaryForm.address2 = {value: ''};
    secondaryForm.city = {value: ''};
    secondaryForm.state = {value: ''};
    secondaryForm.zipCode = {value: ''};
    secondaryForm.country = {value: ''};
    secondaryForm.county = {value: ''};

    return secondaryForm;
  }

  setPhoneNumber = (number) => {
    this.setDirty(true);
    let form = Object.assign({}, this.state.form);
    form.phoneNumber.value = number;
    this.setState({
      form: form
    });
  };
  setSecondaryPhoneNumber = (number) => {
    this.setDirty(true);
    let secondaryForm = Object.assign({}, this.state.secondaryForm);
    secondaryForm.phoneNumber.value = number;
    this.setState({
      secondaryForm: secondaryForm
    });
  };
  setSubscriberDOB = (dobStr) => {
    let form = Object.assign({}, this.state.form);
    form.subscriberDOB.value = dobStr;
    this.setState({
      form: form
    });
  };
  setSecondarySubscriberDOB = (dobStr) => {
    let secondaryForm = Object.assign({}, this.state.secondaryForm);
    secondaryForm.subscriberDOB.value = dobStr;
    this.setState({
      secondaryForm: secondaryForm
    });
  };
  /**
   * Test if the subscriber ID fits the Medicare Beneficiary Identifier (MBI) Format.
   *
   * If we have an MBI format, set the state.possibleMBI value to trigger the confirmation modal.
   *  possibleMBI is used instead of subscriberId so we can control when the modal pops up. We don't
   *  want it to appear while typing so we set possibleMBI when the subscriberid field is blurred.
   *
   * If subscriber ID is not in the MBI format, clear out confirmation modal values.
   */
  checkForMedicareId = (evt) => {
    if (isMedicareOrTricareSubscriberId(this.state.form.subscriberId.value)) {
      this.setState({
        possibleMBI: this.state.form.subscriberId.value
      });
    }
    else if (isMedicareOrTricareSubscriberId(this.state.secondaryForm.subscriberId.value) || this.state.mbiConfirmed) {
      if (this.state.mbiConfirmed) {
        this.setState({
          possibleMBI: null
        });
      } else {
        this.setState({
          possibleMBI: this.state.secondaryForm.subscriberId.value
        });
      }
    }
    else {
      this.setState({
        possibleMBI: null,
        mbiConfirmed: false
      });
    }
  };
  /**
   * If the user confirms that this is Medicare Insurance, set the provider name to 'medicare' and set `mbiConfirmed` to true.
   * Setting mbiConfirmed=true will cause the requisitions' billing methods to be updated on submit.
   */
  confirmMedicareInsurance = () => {
    let form = Object.assign({}, this.state.form);
    form.provider.value = 'medicare';
    form.otherProvider.value = '';

    this.setState({
      mbiConfirmed: true,
      form: form
    });

  };

  validate(form) {
    const validStates = '^([Aa][Ll]|[Aa][Kk]|[Aa][Zz]|[Aa][Rr]|[Cc][Aa]|[Cc][Oo]|[Cc][Tt]|[Dd][Cc]|[Dd][Ee]|[Ff][Ll]|[Gg][Aa]|[Hh][Ii]|[Ii][Dd]|[Ii][Ll]|[Ii][Nn]|[Ii][Aa]|[Kk][Ss]|[Kk][Yy]|[Ll][Aa]|[Mm][Ee]|[Mm][Dd]|[Mm][Aa]|[Mm][Ii]|[Mm][Nn]|[Mm][Ss]|[Mm][Oo]|[Mm][Tt]|[Nn][Ee]|[Nn][Vv]|[Nn][Hh]|[Nn][Jj]|[Nn][Mm]|[Nn][Yy]|[Nn][Cc]|[Nn][Dd]|[Oo][Hh]|[Oo][Kk]|[Oo][Rr]|[Pp][Aa]|[Rr][Ii]|[Ss][Cc]|[Ss][Dd]|[Tt][Nn]|[Tt][Xx]|[Uu][Tt]|[Vv][Tt]|[Vv][Aa]|[Ww][Aa]|[Ww][Vv]|[Ww][Ii]|[Ww][Yy]|[Aa][Bb]|[Bb][Cc]|[Mm][Bb]|[Nn][Bb]|[Nn][Ll]|[Nn][Tt]|[Nn][Ss]|[Nn][Uu]|[Oo][Nn]|[Pp][Ee]|[Qq][Cc]|[Ss][Kk]|[Yy][Tt]|[Aa][Ss]|[Ff][Mm]|[Gg][Uu]|[Mm][Hh]|[Mm][Pp]|[Pp][Ww]|[Pp][Rr]|[Vv][Ii]|[Aa][Aa]|[Aa][Ee]|[Aa][Pp]|)$';
    let validatedForm = Object.assign({}, form);
    Object.values(validatedForm).forEach(prop => {
      prop.valid = true;
      prop.message = '';
    });
    if (!validatedForm.provider.value) {
      validatedForm.provider.valid = false;
      validatedForm.provider.message = 'Please provide an insurance provider.';
    }
    if (!validatedForm.subscriberName.value) {
      validatedForm.subscriberName.valid = false;
      validatedForm.subscriberName.message = 'Please provide a subscriber full name.';
    }
    if (!validatedForm.subscriberId.value) {
      validatedForm.subscriberId.valid = false;
      validatedForm.subscriberId.message = 'Please provide a subscriber ID #/Medicare #.';
    }
    if (!validatedForm.subscriberDOB.value) {
      validatedForm.subscriberDOB.valid = false;
      validatedForm.subscriberDOB.message = 'Please provide a subscriber date of birth.';
    }
    if (!validatedForm.subscriberRelation.value) {
      validatedForm.subscriberRelation.valid = false;
      validatedForm.subscriberRelation.message = 'Please provide a subscriber relationship.';
    }
    if (validatedForm.subscriberRelation.value === 'Other' && !validatedForm.otherSubscriberRelation.value) {
      validatedForm.otherSubscriberRelation.valid = false;
      validatedForm.otherSubscriberRelation.message = 'Please provide the other subscriber relationship.';
    }
    if (validatedForm.provider.value === 'other' && !validatedForm.otherProvider.value) {
      validatedForm.otherProvider.valid = false;
      validatedForm.otherProvider.message = 'Please provide an other insurance provider.';
    }
    if (!(isGovernmentPayorOrder(this.props.gearOrder) || this.state.mbiConfirmed)) {
      if (!validatedForm.address1.value) {
        validatedForm.address1.valid = false;
        validatedForm.address1.message = 'Please provide a street address.';
      }
      if (!validatedForm.city.value) {
        validatedForm.city.valid = false;
        validatedForm.city.message = 'Please provide a city.';
      }
      if (!validatedForm.state.value) {
        validatedForm.state.valid = false;
        validatedForm.state.message = 'Please provide a state.';
      }
      if (!validatedForm.zipCode.value) {
        validatedForm.zipCode.valid = false;
        validatedForm.zipCode.message = 'Please provide a zip code.';
      }
      if (!validatedForm.phoneNumber.value) {
        validatedForm.phoneNumber.valid = false;
        validatedForm.phoneNumber.message = 'Please provide a phone number.';
      } else if (validatedForm.phoneNumber.value.replace(/\D/g, '').length < 11) {
        validatedForm.phoneNumber.valid = false;
        validatedForm.phoneNumber.message = 'Please provide a valid phone number.';
      }
    }
    if (validatedForm.state.value && !(new RegExp(validStates).test(validatedForm.state.value))) {
      validatedForm.state.valid = false;
      validatedForm.state.message = 'Please provide a valid 2 character state abbreviation.';
    }

    return validatedForm;
  }

  submitInsuranceInfo() {
    const validatedForm = this.validate(this.state.form);
    const validatedSecondaryForm = this.validate(this.state.secondaryForm);
    const isValid = Object.values(validatedForm).every(prop => prop.valid) && (!this.state.secondaryInsurance
        || Object.values(validatedSecondaryForm).every(prop => prop.valid));
    this.setState({
      form: validatedForm,
      secondaryForm: validatedSecondaryForm
    }, () => {
      if (!isValid) {
        scrollToMessage();
      }
    });
    if (isValid) {
      let order = Object.assign({}, this.props.gearOrder);

      let insurance = {
        insuranceProvider: this.state.form.provider.value,
        subscriberId: this.state.form.subscriberId.value,
        subscriberName: this.state.form.subscriberName.value,
        subscriberDOB: this.state.form.subscriberDOB.value,
        groupNumber: this.state.form.groupNumber.value,
        subscriberRelation: this.state.form.subscriberRelation.value,
        otherSubscriberRelation: this.state.form.otherSubscriberRelation.value,
        otherProvider: this.state.form.provider.value === 'other' ? this.state.form.otherProvider.value : '',
        phoneNumber: formatPhoneNumberForSave(this.state.form.phoneNumber.value),
        insuranceRank: 0
      };

      let claimsAddress = {
        address1: this.state.form.address1.value,
        address2: this.state.form.address2.value,
        city: this.state.form.city.value,
        state: this.state.form.state.value,
        zipCode: this.state.form.zipCode.value,
        country: this.state.form.country.value,
        county: this.state.form.county.value,
        type: 'claims'
      };

      let addresses = [claimsAddress];

      if (this.state.secondaryInsurance) {
        let secondaryInsurance = {
          insuranceProvider: this.state.secondaryForm.provider.value,
          subscriberId: this.state.secondaryForm.subscriberId.value,
          subscriberName: this.state.secondaryForm.subscriberName.value,
          subscriberDOB: this.state.secondaryForm.subscriberDOB.value,
          groupNumber: this.state.secondaryForm.groupNumber.value,
          subscriberRelation: this.state.secondaryForm.subscriberRelation.value,
          otherSubscriberRelation: this.state.secondaryForm.otherSubscriberRelation.value,
          otherProvider:this.state.secondaryForm.provider.value === 'other' ? this.state.secondaryForm.otherProvider.value : '',
          phoneNumber: formatPhoneNumberForSave(this.state.secondaryForm.phoneNumber.value),
          insuranceRank: 1
        };

        let secondaryClaimsAddress = {
          address1: this.state.secondaryForm.address1.value,
          address2: this.state.secondaryForm.address2.value,
          city: this.state.secondaryForm.city.value,
          state: this.state.secondaryForm.state.value,
          zipCode: this.state.secondaryForm.zipCode.value,
          country: this.state.secondaryForm.country.value,
          county: this.state.secondaryForm.county.value,
          type: 'secondaryClaims'
        };

        addresses.push(secondaryClaimsAddress);
        order['secondaryInsurance'] = secondaryInsurance;
      } else {
        order['secondaryInsurance'] = null;
      }

      order.patientInfo = order.patientInfo || {}; // initialize the patient info if it is null (the patient step was skipped)
      order.patientInfo['addresses'] = addresses;  //shortcut here.  Any address not listed will not be touched
      order['insurance'] = insurance;

      // SCR-6821: Flip billing methods to Medicare if the insurance is confirmed to be Medicare
      if (this.state.mbiConfirmed && order.requisitions) {
        order.requisitions.forEach(req => req.billingMethod = process.env.REACT_APP_BILLING_MEDICARE_VALUE);
      }

      return fetch(process.env.REACT_APP_GEAR_REST_URL + '/order/' + order.id + '/insurance/', {
        credentials: 'include',
        mode: 'cors',
        method: 'PUT',
        timeout: 1000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(order)
      }).then(response => {
        if (response.ok) {
          return response.json();
        } else {
          return Promise.reject('an unexpected error has occurred. please call us at 800-522-4762.');
        }
      }).then(json => {
        if (json.id) {
          this.props.setGearOrder(json);
          this.props.setInsuranceOptions([]);
          this.setDirty(false);
          this.props.updateStep(json, 'insurance', 'Next');

          return Promise.resolve(json);
        } else if (json.errors) {
          this.setState({
            showError: true,
            errorMessage: json.errors[0].field + ' ' + json.errors[0].defaultMessage
          });
          if (this.props.editRef) {
            return Promise.reject('an unexpected error has occurred. please call us at 800-522-4762.');
          }
        }
      }).catch(error => {
        this.setState({
          showError: true,
          errorMessage: 'The network is currently unavailable. Please call us at 800-522-4762.'
        });
        if (this.props.editRef) {
          return Promise.reject('an unexpected error has occurred. please call us at 800-522-4762.');
        } else {
          // Scroll up so the error is visable
          window.scrollTo(0, 0);
        }
      });
    } else {
      if (this.props.editRef) {
        return Promise.reject('ValidationError');
      }
    }
  };

  handleSubmit(event) {
    event.preventDefault();
    if (this.state.pageLocked) {
      this.props.updateStep(this.props.gearOrder, 'insurance', 'Next');
    } else {
      this.submitInsuranceInfo();
    }
  }

  componentDidMount() {
    // Manual ref management because of the limitations of class components
    if (this.props.editRef) {
      this.props.editRef({
        save: this.submitInsuranceInfo,
        reset: () => {},
        isDirty: this.isDirty,
      });
    }
    var inputs = document.getElementsByTagName('input');
    var provider = document.getElementById('insurance-insuranceProvider');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (this.state.pageLocked) {
        input.disabled = true;
      }
    }
    if (this.state.pageLocked) {
      provider.disabled = true;
    }
  }

  setDirty = (value) => {
    this.setState({dirty: value});
    this.props.setDirty(value);
  }
  isDirty = () => {
    return this.state.dirty;
  }

  toggleOptions() {
    this.setState({showOptions: !this.state.showOptions});
  }

  selectOption(option) {
    let form = Object.assign({}, this.state.form);
    let claimsAddress = option.claimsAddress || {};
    form.provider.value = option.insuranceProvider || 'other';
    form.subscriberId.value = option.subscriberId || '';
    form.subscriberName.value = option.subscriberName || '';
    form.subscriberDOB.value = option.subscriberDOB || '';
    form.groupNumber.value = option.groupNumber || '';
    form.subscriberRelation.value = option.subscriberRelation || '';
    form.otherSubscriberRelation.value = option.otherSubscriberRelation || '';
    form.otherProvider.value = option.otherProvider || '';
    form.phoneNumber.value = formatInitialPhoneNumber(option.phoneNumber || '');
    form.address1.value = claimsAddress.address1 || '';
    form.address2.value = claimsAddress.address2 || '';
    form.city.value = claimsAddress.city || '';
    form.state.value = claimsAddress.state || '';
    form.zipCode.value = claimsAddress.zipCode || '';
    form.country.value = claimsAddress.country || '';
    form.county.value = claimsAddress.county || '';
    this.setState({
      form: this.validate(form)
    });
  }

  addUploadedImage(type, name) {
    let order = Object.assign({}, this.props.gearOrder);
    order.attachments[type] = {
      type: type,
      name: name
    };
    this.props.setGearOrder(order);
  }

  removeUploadedImage(type) {
    let order = Object.assign({}, this.props.gearOrder);
    delete order.attachments[type];
    this.props.setGearOrder(order);
  }

  toggleSecondaryInsurance() {
    this.setState((prevState) => {
      if (prevState.secondaryInsurance) {
        return { secondaryInsurance: !prevState.secondaryInsurance, secondaryForm: this.clearSecondaryInsurance()}
      } else {
        return { secondaryInsurance: !prevState.secondaryInsurance }
      }
    });
  }

  render() {
    let insuranceInfo = this.props.gearOrder.insurance;
    if (!insuranceInfo) {
      insuranceInfo = {addresses: [{type: 'claims'}]};
    }
    ;

    let claimsAddress = {};
    if (this.props.gearOrder.patientInfo && this.props.gearOrder.patientInfo.addresses) {
      let patientInfo = this.props.gearOrder.patientInfo;
      for (let i = 0; i < patientInfo.addresses.length; i++) {
        if (patientInfo.addresses[i].type === 'claims') {
          claimsAddress = patientInfo.addresses[i];
        }
      }
    }
    if (!claimsAddress) {
      claimsAddress = {type: 'claims'};
    }

    // Is this order for a Government payor (Medicare/Tricare)
    const isMedicareOrder = isGovernmentPayorOrder(this.props.gearOrder) || this.state.mbiConfirmed;

    var options = [];
    for (var i = 0; i < this.props.options.length; i++) {
      options.push(<InsuranceOption key={i} option={this.props.options[i]} select={(o) => this.selectOption(o)}/>);
    }

    const showMedicareDisclaimer = isMedicareOrder && this.props.userInfo.type === 'prc' && this.props.gearOrder.patientRegistration;

    return (
        <div>
          <div className="row">
            <div className="col-md-12">
              <h2>Insurance Information</h2>
              {!this.props.gearOrder.containsCovidRegistration &&
                  <SkipStep hidden={this.state.pageLocked || this.props.editRef}
                            orderId={this.props.gearOrder.id}
                            stepKey="insurance"
                            getWorkflowStep={this.props.getWorkflowStep}
                            updateStep={this.props.updateStep}
                            setGearOrder={this.props.setGearOrder}
                            setDirty={this.props.setDirty}
                            userType={this.props.userInfo.type}/>
              }
              <div>
                {showMedicareDisclaimer &&
                    <div className="alert alert-info" id="diagnosis-disclaimer">
                      <p>
                        If you have another health insurance card with the word Advantage, Senior, or Medicare Replacement, then you most
                        likely have a Medicare Advantage plan. These plans use commercial insurance to replace your traditional Medicare coverage.
                        If you have a Medicare Advantage plan, please return to the billing options section and change your selection to “Bill
                        Insurance”.
                      </p>
                    </div>
                }
                {this.state.pageLocked && (
                    <AccessionedOrderAlert/>
                )}
                {(isMedicareOrder && this.props.userInfo.type === 'myGDX') &&
                    <MedicareOrderAlert/>
                }
                <div id="validationAlert">
                  <div className="alert alert-danger alert-dismissible" role="alert" hidden={!this.state.showError}>
                    <button type="button" className="close" data-dismiss="alert" aria-label="Close">
                      <span aria-hidden="true"></span></button>
                    <strong>{this.state.errorMessage}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row grid-space-20">
            <div className="col-md-12 col-lg-12">
              <div className="space-top"></div>
              <div>
                <h4>Primary Insurance</h4>
                <div className="separator-2"></div>
              </div>
              <form onSubmit={this.handleSubmit} autoComplete="off" noValidate>
                <div className="row">
                  <div className="col-md-12" hidden={!this.props.options.length} onClick={() => this.toggleOptions()}>
                    				<span hidden={this.state.showOptions}>
                    					<i className="fa fa-plus-square"></i>&nbsp;
                              We found {this.props.options.length} insurance option<span hidden={this.props.options.length === 1}>s</span> for this patient.
                    					Click to review and import information.
                    				</span>
                    <span hidden={!this.state.showOptions}><i className="fa fa-minus-square"></i>&nbsp;
                      We found {this.props.options.length} insurance option<span hidden={this.props.options.length === 1}>s</span> for this patient.
                    					Click an entry to use that insurance information. If the information we have is incomplete, you
                    					may have to enter some additional information before proceeding.
                    				</span>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12" hidden={!this.state.showOptions}>
                    <div>
                      {options}
                    </div>
                  </div>
                </div>
                <div className="form-group has-feedback">
                  <div className="row">
                    <div className={'form-group col-md-4 ' + (this.state.form.provider.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="insurance-insuranceProvider">Insurance Provider<span className="text-danger">*</span></label>
                      <select name="insuranceInfo"
                              id="insurance-insuranceProvider"
                              className="form-control"
                              value={this.state.form.provider.value}
                              onFocus={() => this.setDirty(true)}
                              data-fieldname="provider"
                              onChange={this.setFormValue}>
                        <option value=""></option>
                        {isMedicareOrder && <>
                          <optgroup label="Government Carriers">
                            <option value="medicare">Medicare (not Medicare Advantage)</option>
                            <option value="medicaid">Medicaid / Medicaid Managed Care Plans</option>
                            <option value="tricare">TriCare</option>
                            <option value="medicare_railroad">Railroad Retirement Board</option>
                          </optgroup>
                        </>}
                        {(!isMedicareOrder || (this.state.secondaryInsurance && isMedicareOrder)) && <>
                          <optgroup label="Commercial Carriers">
                            <option data-sched="b" value="aetna">Aetna / Aetna Medicare Advantage</option>
                            <option value="bcbs">Blue Cross Blue Shield (BCBS) / BCBS Medicare Advantage</option>
                            <option value="bcbs-fep">Blue Cross Blue Shield Federal Employees Program (FEP)</option>
                            <option data-sched="a" value="cigna">Cigna</option>
                            <option data-sched="b" value="humana">Humana / Humana Gold / Humana Medicare Advantage</option>
                            <option data-sched="b" value="kaiser">Kaiser / Kaiser Medicare Advantage</option>
                            <option data-sched="b" value="principle">Principle</option>
                            <option data-sched="b" value="tufts">Tufts / Tufts Medicare Advantage</option>
                            <option data-sched="a" value="united-healthcare">United Healthcare</option>
                          </optgroup>
                          <option data-sched="a" value="other">My insurance is not listed / All other companies</option>
                        </>}
                      </select>
                      {this.state.form.provider.valid === false && <span
                          className="validation-message text-danger">{this.state.form.provider.message}</span>}
                    </div>
                    <div className={'form-group col-md-4 ' + (this.state.form.subscriberName.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="insurance-subscriberName">Subscriber Full Name<span
                          className="text-danger">*</span></label>
                      <input className="form-control"
                             name="insuranceInfo"
                             id="insurance-subscriberName"
                             maxLength="50"
                             type="text"
                             value={this.state.form.subscriberName.value}
                             onFocus={() => this.setDirty(true)}
                             autoComplete="new-password"
                             data-fieldname="subscriberName"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.subscriberName.valid === false && <span
                          className="validation-message text-danger">{this.state.form.subscriberName.message}</span>}
                    </div>
                    <div className={'form-group col-md-4 ' + (this.state.form.subscriberId.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="insurance-subscriberId">Subscriber ID #/Medicare #<span
                          className="text-danger">*</span><FieldLevelHelp helpText="<b>How should I enter my ID Number?</b><br/> Please enter your insurance ID number as it appears on your insurance card. If they are not included on your card, please do not enter any dashes or spaces." trigger="hover" large={true} html={true}/></label>
                      <input className="form-control"
                             name="insuranceInfo"
                             id="insurance-subscriberId"
                             maxLength="50"
                             type="text"
                             pattern="[a-zA-Z0-9]+"
                             value={this.state.form.subscriberId.value}
                             onFocus={() => this.setDirty(true)}
                             onBlur={this.checkForMedicareId}
                             autoComplete="off"
                             data-fieldname="subscriberId"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.subscriberId.valid === false && <span
                          className="validation-message text-danger">{this.state.form.subscriberId.message}</span>}
                    </div>
                  </div>
                  <div className="row">
                    <div className={'form-group col-md-12 ' + (this.state.form.otherProvider.valid === false ? 'has-error' : '')}
                         hidden={this.state.form.provider.value !== 'other'}>
                      <label className="control-label" htmlFor="insurance-otherProvider"> Other Insurance Provider<span
                          className="text-danger">*</span></label>
                      <input className="form-control"
                             name="insuranceInfo"
                             id="insurance-otherProvider"
                             maxLength="100"
                             type="text"
                             value={this.state.form.otherProvider.value}
                             onFocus={() => this.setDirty(true)}
                             data-fieldname="otherProvider"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.otherProvider.valid === false && <span
                          className="validation-message text-danger">{this.state.form.otherProvider.message}</span>}
                    </div>
                  </div>
                  <div className="row">
                    <div className="form-group col-md-4">
                      {/* Add <br /> to align items with DOB input fields (DOB has extra label text) */}
                      <span className="hidden-xs hidden-sm"><br/></span>
                      <label className="control-label" htmlFor="insurance-groupNumber">Group #</label>
                      <input className="form-control"
                             name="insuranceInfo"
                             id="insurance-groupNumber"
                             maxLength="50"
                             type="text"
                             pattern="[a-zA-Z0-9]*"
                             value={this.state.form.groupNumber.value}
                             onFocus={() => this.setDirty(true)}
                             autoComplete="new-password"
                             data-fieldname="groupNumber"
                             onChange={this.setFormValue}>
                      </input>
                    </div>
                    <div className={'form-group col-md-4 ' + (this.state.form.subscriberDOB.valid === false ? 'has-error' : '')}>
                      <label className="control-label mb-0" htmlFor="insurance-subscriberDOB">Subscriber Date Of Birth<span
                          className="text-danger">*</span></label>
                      <DateOfBirthInput disabled={this.state.pageLocked} value={this.state.form.subscriberDOB.value} onChange={this.setSubscriberDOB}
                                        displayDateString={false} displayAge={false}/>
                      {this.state.form.subscriberDOB.valid === false && <span className="validation-message text-danger"
                                                                              style={{display: 'block'}}>{this.state.form.subscriberDOB.message}</span>}
                    </div>
                    <div className={'form-group col-md-4 ' + (this.state.form.subscriberRelation.valid === false ? 'has-error' : '')}>
                      {/* Add <br /> to align items with DOB input fields (DOB has extra label text) */}
                      <span className="hidden-xs hidden-sm"><br/></span>
                      <label className="control-label" htmlFor="insurance-subscriberRelation">Subscriber Relationship<span
                          className="text-danger">*</span></label>
                      <select className="form-control"
                              name="insuranceInfo"
                              id="insurance-subscriberRelation"
                              value={this.state.form.subscriberRelation.value}
                              onFocus={() => this.setDirty(true)}
                              data-fieldname="subscriberRelation"
                              onChange={this.setFormValue}
                              disabled={this.state.pageLocked}>
                        <option value=""></option>
                        <option value="Self">Self</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Other">Other</option>
                      </select>
                      {this.state.form.subscriberRelation.valid === false && <span
                          className="validation-message text-danger">{this.state.form.subscriberRelation.message}</span>}
                    </div>
                  </div>
                  <div className="row">
                    <div className={'form-group col-md-12 ' + (this.state.form.otherSubscriberRelation.valid === false ? 'has-error' : '')}
                         hidden={this.state.form.subscriberRelation.value !== 'Other'}>
                      <label className="control-label" htmlFor="insurance-otherSubscriberRelation"> Other Subscriber Relationship<span
                          className="text-danger">*</span></label>
                      <input className="form-control"
                             name="insuranceInfo"
                             id="insurance-otherSubscriberRelation"
                             maxLength="50"
                             type="text"
                             value={this.state.form.otherSubscriberRelation.value}
                             onFocus={() => this.setDirty(true)}
                             data-fieldname="otherSubscriberRelation"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.otherSubscriberRelation.valid === false && <span
                          className="validation-message text-danger">{this.state.form.otherSubscriberRelation.message}</span>}
                    </div>
                  </div>
                  <div className="row">
                    <div className={'form-group col-md-12 ' + (this.state.form.address1.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="claims-address1">Insurance Carrier Claims Address{!isMedicareOrder && <span
                          className="text-danger">*</span>}<FieldLevelHelp helpText="<b>Where can I find this?</b><br/>This is usually listed on the back of your insurance card." trigger="hover" large={true} html={true}/></label>
                      <input className="form-control"
                             name="claimsAddress"
                             id="claims-address1"
                             maxLength="50"
                             type="text"
                             value={this.state.form.address1.value}
                             onFocus={() => this.setDirty(true)}
                             autoComplete="off"
                             data-fieldname="address1"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.address1.valid === false && <span
                          className="validation-message text-danger">{this.state.form.address1.message}</span>}
                    </div>
                  </div>
                  <div className="row">
                    <div className={'form-group col-md-4 ' + (this.state.form.city.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="claims-city">Claims City{!isMedicareOrder && <span
                          className="text-danger">*</span>}</label>
                      <input className="form-control"
                             name="claimsAddress"
                             id="claims-city"
                             maxLength="50"
                             type="text"
                             value={this.state.form.city.value}
                             onFocus={() => this.setDirty(true)}
                             autoComplete="off"
                             data-fieldname="city"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.city.valid === false && <span className="validation-message text-danger">{this.state.form.city.message}</span>}
                    </div>
                    <div className={'form-group col-md-4 ' + (this.state.form.state.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="claims-state">Claims State{!isMedicareOrder && <span
                          className="text-danger">*</span>}</label>
                      <input className="form-control"
                             name="claimsAddress"
                             id="claims-state"
                             list="states"
                             maxLength="2"
                             title="Enter a valid 2 character state abbreviaton."
                             type="text"
                             value={this.state.form.state.value}
                             onFocus={() => this.setDirty(true)}
                             autoComplete="new-password"
                             data-fieldname="state"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.state.valid === false && <span
                          className="validation-message text-danger">{this.state.form.state.message}</span>}
                    </div>
                    <div className={'form-group col-md-4 ' + (this.state.form.zipCode.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="claims-zipCode">Claims Zip Code{!isMedicareOrder && <span
                          className="text-danger">*</span>}</label>
                      <input className="form-control"
                             name="claimsAddress"
                             id="claims-zipCode"
                             maxLength="5"
                             type="text"
                             value={this.state.form.zipCode.value}
                             onFocus={() => this.setDirty(true)}
                             autoComplete="new-password"
                             data-fieldname="zipCode"
                             onChange={this.setFormValue}>
                      </input>
                      {this.state.form.zipCode.valid === false && <span
                          className="validation-message text-danger">{this.state.form.zipCode.message}</span>}
                    </div>
                  </div>
                  <div className="row">
                    <div className={'form-group col-md-4 ' + (this.state.form.phoneNumber.valid === false ? 'has-error' : '')}>
                      <label className="control-label" htmlFor="insurance-phoneNumber">Claims Phone Number{!isMedicareOrder && <span
                          className="text-danger">*</span>}</label>
                      <PhoneInput
                          defaultCountry="US"
                          className="form-control"
                          name="insuranceInfo"
                          id="insurance-phoneNumber"
                          value={this.state.form.phoneNumber?.value}
                          autoComplete="new-password"
                          data-fieldname="phoneNumber"
                          onChange={this.setPhoneNumber}/>
                      {this.state.form.phoneNumber.valid === false && <span
                          className="validation-message text-danger">{this.state.form.phoneNumber.message}</span>}
                    </div>
                  </div>
                  <br/>
                  <div className="row">
                    <div className="col-xs-12">
                      <button className="btn btn-primary radius btn-sm margin-clear" type="button"
                              onClick={this.toggleSecondaryInsurance}>
                        {this.state.secondaryInsurance ? <div><i className="fa fa-minus"></i> Remove Secondary Insurance</div>  : <div><i className="fa fa-plus"></i> Add Secondary Insurance</div>}
                      </button>
                    </div>
                  </div>
                  {this.state.secondaryInsurance && <div className="row">
                    <div className="row">
                      <div className="col-md-12 col-lg-12">
                        <div className="space-top"></div>
                        <h4>Secondary Insurance <small>(optional)</small></h4>
                        <div className="separator-2"></div>
                      </div>
                      <div className="col-md-12 col-lg-12">
                        <p>
                          Please include information below on your secondary insurance, including attaching copies of your insurance cards below if able.
                          Please note that Genova Diagnostics does not file secondary insurance claims, unless you are covered by Traditional Medicare or Tricare.
                        </p>
                      </div>
                    </div>
                    <div className="row form-group has-feedback" style={{
                      padding: '1em',
                      borderRadius: '5px',
                      boxShadow: 'inset 0 0 5px rgb(0 0 0 / 6%), 0 0 5px rgb(0 0 0 / 6%)',
                      backgroundColor: '#c6e8fa'
                    }}>
                      <div className="row">
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.provider.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="insurance-insuranceProvider">Insurance Provider<span
                              className="text-danger">*</span></label>
                          <select name="insuranceInfo"
                                  id="insurance-insuranceProvider"
                                  className="form-control"
                                  value={this.state.secondaryForm.provider.value}
                                  onFocus={() => this.setDirty(true)}
                                  data-fieldname="provider"
                                  onChange={this.setSecondaryProvider}>
                            <option value=""></option>
                            <optgroup label="Commercial Carriers">
                              <option data-sched="b" value="aetna">Aetna / Aetna Medicare Advantage</option>
                              <option value="bcbs">Blue Cross Blue Shield (BCBS) / BCBS Medicare Advantage</option>
                              <option value="bcbs-fep">Blue Cross Blue Shield Federal Employees Program (FEP)</option>
                              <option data-sched="a" value="cigna">Cigna</option>
                              <option data-sched="b" value="humana">Humana / Humana Gold / Humana Medicare Advantage</option>
                              <option data-sched="b" value="kaiser">Kaiser / Kaiser Medicare Advantage</option>
                              <option data-sched="b" value="principle">Principle</option>
                              <option data-sched="b" value="tufts">Tufts / Tufts Medicare Advantage</option>
                              <option data-sched="a" value="united-healthcare">United Healthcare</option>
                            </optgroup>
                            <optgroup label="Government Carriers">
                              <option value="medicare">Medicare (not Medicare Advantage)</option>
                              <option value="medicaid">Medicaid / Medicaid Managed Care Plans</option>
                              <option value="tricare">TriCare</option>
                              <option value="medicare_railroad">Railroad Retirement Board</option>
                            </optgroup>
                            <option data-sched="a" value="other">My insurance is not listed / All other companies</option>
                          </select>
                          {this.state.secondaryForm.provider.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.provider.message}</span>}
                        </div>
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.subscriberName.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="insurance-subscriberName">Subscriber Full Name<span
                              className="text-danger">*</span></label>
                          <input className="form-control"
                                 name="insuranceInfo"
                                 id="insurance-subscriberName"
                                 maxLength="50"
                                 type="text"
                                 value={this.state.secondaryForm.subscriberName.value}
                                 onFocus={() => this.setDirty(true)}
                                 autoComplete="new-password"
                                 data-fieldname="subscriberName"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.subscriberName.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.subscriberName.message}</span>}
                        </div>
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.subscriberId.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="insurance-subscriberId">Subscriber ID #/Medicare #<span
                              className="text-danger">*</span><FieldLevelHelp helpText="<b>How should I enter my ID Number?</b><br/> Please enter your insurance ID number as it appears on your insurance card. If they are not included on your card, please do not enter any dashes or spaces." trigger="hover" large={true} html={true}/></label>
                          <input className="form-control"
                                 name="insuranceInfo"
                                 id="insurance-subscriberId"
                                 maxLength="50"
                                 type="text"
                                 pattern="[a-zA-Z0-9]+"
                                 value={this.state.secondaryForm.subscriberId.value}
                                 onFocus={() => this.setDirty(true)}
                                 onBlur={this.checkForMedicareId}
                                 autoComplete="off"
                                 data-fieldname="subscriberId"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.subscriberId.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.subscriberId.message}</span>}
                        </div>
                      </div>
                      <div className="row">
                        <div className={'form-group col-md-12 ' + (this.state.secondaryForm.otherProvider.valid === false ? 'has-error' : '')}
                             hidden={this.state.secondaryForm.provider.value !== 'other'}>
                          <label className="control-label" htmlFor="insurance-otherProvider"> Other Insurance Provider<span
                              className="text-danger">*</span></label>
                          <input className="form-control"
                                 name="insuranceInfo"
                                 id="insurance-otherProvider"
                                 maxLength="100"
                                 type="text"
                                 value={this.state.secondaryForm.otherProvider.value}
                                 onFocus={() => this.setDirty(true)}
                                 data-fieldname="otherProvider"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.otherProvider.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.otherProvider.message}</span>}
                        </div>
                      </div>
                      <div className="row">
                        <div className="form-group col-md-4">
                          {/* Add <br /> to align items with DOB input fields (DOB has extra label text) */}
                          <span className="hidden-xs hidden-sm"><br/></span>
                          <label className="control-label" htmlFor="insurance-groupNumber">Group #</label>
                          <input className="form-control"
                                 name="insuranceInfo"
                                 id="insurance-groupNumber"
                                 maxLength="50"
                                 type="text"
                                 pattern="[a-zA-Z0-9]*"
                                 value={this.state.secondaryForm.groupNumber.value}
                                 onFocus={() => this.setDirty(true)}
                                 autoComplete="new-password"
                                 data-fieldname="groupNumber"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                        </div>
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.subscriberDOB.valid === false ? 'has-error' : '')}>
                          <label className="control-label mb-0" htmlFor="insurance-subscriberDOB">Subscriber Date Of Birth<span
                              className="text-danger">*</span></label>
                          <DateOfBirthInput disabled={this.state.pageLocked} value={this.state.secondaryForm.subscriberDOB.value}
                                            onChange={this.setSecondarySubscriberDOB}
                                            displayDateString={false} displayAge={false}/>
                          {this.state.secondaryForm.subscriberDOB.valid === false && <span className="validation-message text-danger"
                                                                                  style={{display: 'block'}}>{this.state.secondaryForm.subscriberDOB.message}</span>}
                        </div>
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.subscriberRelation.valid === false ? 'has-error' : '')}>
                          {/* Add <br /> to align items with DOB input fields (DOB has extra label text) */}
                          <span className="hidden-xs hidden-sm"><br/></span>
                          <label className="control-label" htmlFor="insurance-subscriberRelation">Subscriber Relationship<span
                              className="text-danger">*</span></label>
                          <select className="form-control"
                                  name="insuranceInfo"
                                  id="insurance-subscriberRelation"
                                  value={this.state.secondaryForm.subscriberRelation.value}
                                  onFocus={() => this.setDirty(true)}
                                  data-fieldname="subscriberRelation"
                                  onChange={this.setSecondaryFormValue}
                                  disabled={this.state.pageLocked}>
                            <option value=""></option>
                            <option value="Self">Self</option>
                            <option value="Spouse">Spouse</option>
                            <option value="Other">Other</option>
                          </select>
                          {this.state.secondaryForm.subscriberRelation.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.subscriberRelation.message}</span>}
                        </div>
                      </div>
                      <div className="row">
                        <div className={'form-group col-md-12 ' + (this.state.secondaryForm.otherSubscriberRelation.valid === false ? 'has-error' : '')}
                             hidden={this.state.secondaryForm.subscriberRelation.value !== 'Other'}>
                          <label className="control-label" htmlFor="insurance-otherSubscriberRelation"> Other Subscriber Relationship<span
                              className="text-danger">*</span></label>
                          <input className="form-control"
                                 name="insuranceInfo"
                                 id="insurance-otherSubscriberRelation"
                                 maxLength="50"
                                 type="text"
                                 value={this.state.secondaryForm.otherSubscriberRelation.value}
                                 onFocus={() => this.setDirty(true)}
                                 data-fieldname="otherSubscriberRelation"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.otherSubscriberRelation.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.otherSubscriberRelation.message}</span>}
                        </div>
                      </div>
                      <div className="row">
                        <div className={'form-group col-md-12 ' + (this.state.secondaryForm.address1.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="claims-address1">Insurance Carrier Claims Address{!isMedicareOrder && <span
                              className="text-danger">*</span>}<FieldLevelHelp helpText="<b>Where can I find this?</b><br/>This is usually listed on the back of your insurance card." trigger="hover" large={true} html={true}/></label>
                          <input className="form-control"
                                 name="claimsAddress"
                                 id="claims-address1"
                                 maxLength="50"
                                 type="text"
                                 value={this.state.secondaryForm.address1.value}
                                 onFocus={() => this.setDirty(true)}
                                 autoComplete="off"
                                 data-fieldname="address1"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.address1.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.address1.message}</span>}
                        </div>
                      </div>
                      <div className="row">
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.city.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="claims-city">Claims City{!isMedicareOrder && <span
                              className="text-danger">*</span>}</label>
                          <input className="form-control"
                                 name="claimsAddress"
                                 id="claims-city"
                                 maxLength="50"
                                 type="text"
                                 value={this.state.secondaryForm.city.value}
                                 onFocus={() => this.setDirty(true)}
                                 autoComplete="off"
                                 data-fieldname="city"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.city.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.city.message}</span>}
                        </div>
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.state.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="claims-state">Claims State{!isMedicareOrder && <span
                              className="text-danger">*</span>}</label>
                          <input className="form-control"
                                 name="claimsAddress"
                                 id="claims-state"
                                 list="states"
                                 maxLength="2"
                                 title="Enter a valid 2 character state abbreviaton."
                                 type="text"
                                 value={this.state.secondaryForm.state.value}
                                 onFocus={() => this.setDirty(true)}
                                 autoComplete="new-password"
                                 data-fieldname="state"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.state.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.state.message}</span>}
                        </div>
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.zipCode.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="claims-zipCode">Claims Zip Code{!isMedicareOrder && <span
                              className="text-danger">*</span>}</label>
                          <input className="form-control"
                                 name="claimsAddress"
                                 id="claims-zipCode"
                                 maxLength="5"
                                 type="text"
                                 value={this.state.secondaryForm.zipCode.value}
                                 onFocus={() => this.setDirty(true)}
                                 autoComplete="new-password"
                                 data-fieldname="zipCode"
                                 onChange={this.setSecondaryFormValue}>
                          </input>
                          {this.state.secondaryForm.zipCode.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.zipCode.message}</span>}
                        </div>
                      </div>
                      <div className="row">
                        <div className={'form-group col-md-4 ' + (this.state.secondaryForm.phoneNumber.valid === false ? 'has-error' : '')}>
                          <label className="control-label" htmlFor="insurance-phoneNumber">Claims Phone Number{!isMedicareOrder && <span
                              className="text-danger">*</span>}</label>
                          <PhoneInput
                              defaultCountry="US"
                              className="form-control"
                              name="insuranceInfo"
                              id="insurance-phoneNumber"
                              value={this.state.secondaryForm.phoneNumber?.value}
                              autoComplete="new-password"
                              data-fieldname="phoneNumber"
                              onChange={this.setSecondaryPhoneNumber}/>
                          {this.state.secondaryForm.phoneNumber.valid === false && <span
                              className="validation-message text-danger">{this.state.secondaryForm.phoneNumber.message}</span>}
                        </div>
                      </div>
                    </div>
                  </div>}
                  <div className="row">
                    <div className="col-md-12 col-lg-12">
                      <div className="space-top"></div>
                      <h4>Upload ID Cards <small>(optional)</small></h4>
                      <div className="separator-2"></div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12 col-lg-12" id="imageValidationAlert">
                      <div className="col-md-12 col-lg-12 alert alert-danger alert-dismissible" role="alert"
                           hidden={!this.state.showUploadError}
                           onClick={() => {
                             this.setState({
                               showUploadError: false,
                               uploadErrorMessage: ''
                             });
                           }}
                      >
                        <button type="button" className="close" data-dismiss="alert" aria-label="Close">
                          <span aria-hidden="true"></span>
                        </button>
                        <strong>{this.state.uploadErrorMessage}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12 col-lg-12">
                      <ImageUpload
                          gearOrder={this.props.gearOrder}
                          showUploadError={this.showUploadError}
                          addUploadedImage={this.addUploadedImage}
                          removeUploadedImage={this.removeUploadedImage}
                      />
                    </div>
                  </div>
                </div>
                <div className="space"></div>
                <RequiredFieldDisclaimer/>
                {!this.props.editRef && <div className="row sticky-bottom">
                  <div className="col-xs-12">
                    <button className="btn btn-white radius btn-sm margin-clear" type="button" onClick={this.goBack}>
                      <i className="pl-10 fa fa-arrow-circle-left"></i> &nbsp;
                      Back
                    </button>
                    <button className="btn btn-primary radius btn-sm margin-clear pull-right" type="submit">
                      Next
                      <i className="pl-10 fa fa-arrow-circle-right"></i>
                    </button>
                  </div>
                </div>}
              </form>
            </div>
          </div>
          {(!isMedicareOrder) && (
              <MedicareConfirmation
                  subsriberId={this.state.possibleMBI}
                  onConfirm={this.confirmMedicareInsurance}
                  gearOrder={this.props.gearOrder}
                  userInfo={this.props.userInfo}
                  updateStep={this.props.updateStep}
              />
          )}
        </div>
    );
  }
}

// wrap the component in the workflow HOC to get access to common workflow logic
export default withWorkflow(Insurance);
